import React from "react";
import PropTypes from "prop-types";
import SearchBox from "./SearchBox";
import {
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListSubheader,
  Paper,
  TablePagination,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import withIntl from "../../utils/withIntl";

const useStyles = makeStyles(() => ({
  checkBox: { fontSize: "0.7rem" },
  listSubHeader: { padding: 0, paddingTop: 20 },
}));

function SimpleItemList(props) {
  const classes = useStyles();
  const [searchValue, setSearchValue] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [filters, setFilters] = React.useState(props.itemFields?.map(() => true));

  let screenSize = window.innerWidth;
  const [buttonView, setButtonView] = React.useState(screenSize < 600);

  const groups = props.groups;

  let totalItemCount = props.groups.reduce((count, group) => {
    count += group.items.length;
    return count;
  }, 0);

  let numberOfItems = 0;
  let skippedNumberOfItems = 0;
  let numberOfItemsToSkip = page * rowsPerPage;
  const pagedGroups = groups.reduce((groups, group) => {
    let newGroup = { ...group };
    newGroup.items = newGroup.items.filter((item) => {
      if (searchValue.length > 0) {
        let found = item?.searchTags.some((tag) => {
          return tag.toString().toLowerCase().includes(searchValue.toLowerCase());
        });
        if (found === false) {
          totalItemCount--;
          return false;
        }
      }
      if (skippedNumberOfItems < numberOfItemsToSkip) {
        skippedNumberOfItems += 1;
        return false;
      } else if (numberOfItems < rowsPerPage) {
        numberOfItems += 1;
        return true;
      } else {
        return false;
      }
    });
    if (newGroup.items.length > 0) {
      groups.push(newGroup);
    }
    return groups;
  }, []);

  return (
    <Paper>
      <div style={{ display: "flex", flex: "1 1 0%", flexDirection: "column", margin: 8 }}>
        <Typography variant={"h5"}>{props.title}</Typography>
        <Divider style={{ marginBottom: 10 }} />
        <div style={{ display: "flex", width: "100%", flexDirection: "row", flexFlow: "wrap" }}>
          <SearchBox
            searchValue={searchValue}
            onChange={(value) => {
              setSearchValue(value);
              setPage(0);
            }}
          />
          {filters?.map((filterValue, index) => {
            return (
              <FormControlLabel
                classes={{ label: classes.checkBox }}
                key={index}
                control={
                  <Checkbox
                    size={"small"}
                    checked={filterValue}
                    onChange={() => {
                      filters[index] = !filters[index];
                      setFilters([...filters]);
                    }}
                  />
                }
                label={props.itemFields[index].name}
                labelPlacement={"top"}
              />
            );
          })}

          {screenSize > 600 && (
            <FormControlLabel
              classes={{ label: classes.checkBox }}
              key={"show_whole_button"}
              control={
                <Checkbox
                  size={"small"}
                  checked={buttonView}
                  onChange={(event) => {
                    setButtonView(event.target.checked);
                  }}
                />
              }
              label={props.intl.formatMessage({ id: "show_whole_button" })}
              labelPlacement={"top"}
            />
          )}
        </div>
      </div>
      <TablePagination
        component="div"
        count={totalItemCount}
        page={page}
        onChangePage={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={(e) => {
          setPage(0);
          setRowsPerPage(parseInt(e.target.value, 10));
        }}
      />
      <List>
        {pagedGroups.map((group) => (
          <li>
            <ListSubheader classes={{ root: classes.listSubHeader }}>
              <Grid container>
                <Grid container item xs={10} sm={7}>
                  <div style={{ width: "70%", paddingLeft: 10 }}>{group.name}</div>
                  {props.itemFields.map((field, index) => {
                    if (filters[index] && field.valueType === "text") {
                      return <div style={{ width: "15%" }}>{field.key}</div>;
                    }
                  })}
                </Grid>

                {!buttonView ? (
                  <Grid item container xs={10} sm={4}>
                    {props.itemFields.map((field, index) => {
                      if (filters[index] && field.valueType === "array") {
                        return <div style={{ width: "50%" }}>{field.key}</div>;
                      }
                    })}
                  </Grid>
                ) : (
                  <Grid sm={4} />
                )}

                <Grid item xs={2} sm={1}>
                  {group.actionButton && (
                    <IconButton
                      size={"small"}
                      onClick={() => {
                        let groupFound = groups.find((originalGroup) => originalGroup.id === group.id);
                        props.onGroupActionButton(groupFound.items.map((item) => item.id));
                      }}
                    >
                      {group.actionButton}
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </ListSubheader>
            {group.items.map((item) => (
              <ListItem classes={{ root: props.classes?.listItem }}>
                <Grid container alignItems={"center"}>
                  <Grid container xs={10} sm={7}>
                    <div style={{ width: "70%", paddingLeft: 10 }}>{item.name}</div>
                    {props.itemFields?.map((field, index) => {
                      if (filters[index]) {
                        if (field.valueType === "text") {
                          let value = item[field.key];
                          if (field.value) {
                            value = field.value(value);
                          }
                          return (
                            <div style={{ width: "15%" }}>
                              <Typography style={{ color: "gray" }} variant={"caption"}>
                                {value}
                              </Typography>
                            </div>
                          );
                        }
                      }
                      return null;
                    })}
                  </Grid>
                  {buttonView && (
                    <>
                      <Grid sm={4} />
                      <Grid item xs={2} sm={1}>
                        {item.actionButton ? (
                          <IconButton
                            size={"small"}
                            onClick={() => {
                              props.onActionButton(item.id);
                            }}
                          >
                            {item.actionButton}
                          </IconButton>
                        ) : null}
                      </Grid>
                    </>
                  )}
                  <Grid container item xs={10} sm={buttonView ? 10 : 4}>
                    {props.itemFields?.map((field, index) => {
                      if (filters[index]) {
                        if (item[field.key] instanceof Array) {
                          return item[field.key].map((text) => (
                            <Tooltip title={text}>
                              <Chip
                                size={"small"}
                                label={buttonView ? text : text.substring(0, 2)}
                                classes={{ label: classes.checkBox }}
                                color={field.color ? field.color : "primary"}
                                variant={"outlined"}
                              />
                            </Tooltip>
                          ));
                        }
                      }
                      return null;
                    })}
                  </Grid>
                  {!buttonView && (
                    <>
                      <Grid item xs={2} sm={1}>
                        {item.actionButton ? (
                          <IconButton
                            size={"small"}
                            onClick={() => {
                              props.onActionButton(item.id);
                            }}
                          >
                            {item.actionButton}
                          </IconButton>
                        ) : null}
                      </Grid>
                    </>
                  )}
                </Grid>
              </ListItem>
            ))}
          </li>
        ))}
      </List>
      <TablePagination
        component="div"
        count={totalItemCount}
        page={page}
        onChangePage={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={(e) => {
          setPage(0);
          setRowsPerPage(parseInt(e.target.value, 10));
        }}
      />
    </Paper>
  );
}

SimpleItemList.propTypes = {
  groups: PropTypes.array.isRequired,
};

export default withIntl(SimpleItemList);
